import {useEffect, useRef, useState} from 'react';

import {CheckOutlined} from '@mui/icons-material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckIcon from '@mui/icons-material/Check';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import {Grid, Typography} from '@mui/material';
import Box from '@mui/material/Box';
import {marked} from 'marked';
import PropTypes from 'prop-types';

import {REGULAR_EXPRESSIONS, REGULAR_EXPRESSIONS_ERROR_TEXTS, SUMMARY_MAXIMUM_NUMBER_CHARS} from '../../../../const';
import useReport from '../../../../hooks/providers/useReport';
import useReportSummary from '../../../../hooks/providers/useReportSummary';
import Button from '../../../form/buttons/Button/Button';
import Checkbox from '../../../form/Checkbox/Checkbox';
import ErrorMessage from '../../../form/ErrorMessage/ErrorMessage';
import MyRichTextEditor from '../../../form/RichTextEditor/RichTextEditor';

const UpdateSummaryForm = ({openGenerateAnalysisModal}) => {
  const {summaryBeforeUpdate, summary, setSummary, streamedSummary, shouldReplaceExistingSummary, isGeneratingAnalysis, setReportSummaryModalOpen, updateSummary, currentReportPageSummary} =
    useReportSummary();
  const {selectedReport, reports, reportSelectedPage, selectedProject: project} = useReport();

  const [initialSummary, setInitialSummary] = useState('');
  const [shouldGenerateAudio, setShouldGenerateAudio] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [summaryConvertedToHtml, setSummaryConvertedToHtml] = useState('');

  const rteRef = useRef(null);
  const editor = rteRef.current?.editor;

  const summaryPristine = summary === initialSummary;
  const hasSummaryTextError = summary !== '' && !REGULAR_EXPRESSIONS.textWithRangeChars(0, SUMMARY_MAXIMUM_NUMBER_CHARS).test(summary);
  const submitButtonDisabled = summaryPristine || hasSummaryTextError || !summary || isSubmitting;

  // This hook sets default analysis value from PBI report visual
  useEffect(() => {
    if (currentReportPageSummary) {
      setSummaryConvertedToHtml(currentReportPageSummary);

      if (!editor) return;
      const textContent = editor.getText();
      setSummary(textContent);
      setInitialSummary(textContent);
    }
  }, [editor, currentReportPageSummary]);

  useEffect(() => {
    if (!editor) return;

    const setContent = () => {
      const textContent = editor.getText();
      const htmlContent = editor.getHTML();
      setSummary(textContent);
      setSummaryConvertedToHtml(htmlContent);
    };

    editor.on('update', setContent);

    return () => {
      editor.off('update', setContent);
    };
  }, [editor]);

  useEffect(() => {
    (async () => {
      if (streamedSummary.length === 0) return;

      if (shouldReplaceExistingSummary) {
        setSummary(streamedSummary.join(''));
      } else {
        setSummary(`${summaryBeforeUpdate} ${streamedSummary.join('')}`);
      }
    })();
  }, [streamedSummary]);

  useEffect(() => {
    (async () => {
      if (streamedSummary.length > 0) {
        const streamedSummaryToString = streamedSummary.join('');
        const markdownToHtmlSummary = await marked(streamedSummaryToString);
        setSummaryConvertedToHtml(markdownToHtmlSummary);
      }
    })();
  }, [streamedSummary]);

  const onSubmit = async () => {
    setIsSubmitting(true);
    setReportSummaryModalOpen(false);
    const reportName = reports?.find(r => r.report_id === selectedReport.config.id).report_name;

    await updateSummary({
      siren: project.siren,
      newSummary: summaryConvertedToHtml,
      withAudio: shouldGenerateAudio,
      reportName,
      pageName: reportSelectedPage.displayName
    });

    setIsSubmitting(false);
  };

  return (
    <>
      <MyRichTextEditor ref={rteRef} isStreaming={isGeneratingAnalysis} content={summaryConvertedToHtml} />

      <Grid container mt={1.5} mb={2} alignItems="center" justifyContent="space-between">
        <Typography fontSize={13} color={hasSummaryTextError ? 'error' : 'text.secondary'}>
          {summary.length} caractères
        </Typography>
        <Typography fontSize={13} color={hasSummaryTextError ? 'error' : 'text.secondary'}>
          Maximum {SUMMARY_MAXIMUM_NUMBER_CHARS} caractères
        </Typography>
      </Grid>

      <Grid container justifyContent="flex-end" flexDirection="column" my={2}>
        <Grid item>
          <Grid container alignItems="center" gap={0.5}>
            <Checkbox
              label={
                <Grid container alignItems="center" gap={0.5}>
                  <VolumeUpIcon fontSize="small" color="primary" />
                  <Typography fontSize={15} color="text.primary">
                    Générer l'audio du commentaire
                  </Typography>
                </Grid>
              }
              checked={shouldGenerateAudio}
              onChange={e => setShouldGenerateAudio(e.target.checked)}
            />
          </Grid>
        </Grid>
        <Typography color="text.secondary" fontSize={11} mt={-0.5} ml={4}>
          Un fichier audio sera créé à partir de votre texte
        </Typography>
      </Grid>
      <Grid
        container
        justifyContent="center"
        my={3}
        mb={1.5}
        gap={{
          xs: 0.5,
          sm: 2
        }}
        flexWrap="nowrap"
      >
        <Button
          sx={{
            maxWidth: 300,
            fontSize: {
              xs: 12,
              sm: 14
            }
          }}
          fullWidth
          startIcon={<AutoFixHighIcon />}
          size="large"
          variant="contained"
          color="primary"
          onClick={openGenerateAnalysisModal}
          disabled={isGeneratingAnalysis}
        >
          Générer un commentaire
        </Button>
        <Button
          sx={{
            maxWidth: 300,
            fontSize: {
              xs: 12,
              sm: 14
            }
          }}
          fullWidth
          startIcon={<CheckOutlined />}
          size="large"
          variant="contained"
          color="success"
          disabled={submitButtonDisabled}
          onClick={onSubmit}
        >
          Valider le commentaire
        </Button>
      </Grid>
    </>
  );
};

UpdateSummaryForm.propTypes = {
  openGenerateAnalysisModal: PropTypes.func.isRequired
};

export default UpdateSummaryForm;
